import { persist } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

/**
 * A tour that ships with the product. Its steps AND its copy live statically in
 * `./tours.ts` + `./content.ts`, so every product tour is guaranteed to have a
 * `triggerLabel` for `TourTrigger`.
 */
export type ProductTourId = 'case-investigation' | 'behavioral-insights'

/**
 * Identifies a guided tour. Product tours resolve from the static registry in
 * `./tours.ts`; ids outside `ProductTourId` resolve from the runtime registry
 * (`registerTour`) — that's how the demo tour hands its definition to the engine
 * from a lazy, demo-only chunk without any shared module importing it.
 *
 * Add a product tour by extending `ProductTourId` and the static registry — no
 * call-site wiring.
 */
export type TourId = ProductTourId | 'demo-walkthrough'

type GuidedTourState = {
	/** The tour currently running, or null. Ephemeral — never persisted. */
	activeTourId: TourId | null
	/**
	 * True while a step's own `before` hook is driving a route change, i.e. the
	 * tour navigated the visitor, the visitor didn't navigate away. The host uses
	 * it to tell the two apart: scripted changes resume the tour on the new route,
	 * user-initiated ones still dismiss it. Ephemeral — never persisted.
	 */
	isScriptedNavigation: boolean
	/**
	 * Per-tour "taken at least once" flags. Persisted to localStorage so a tour's
	 * attention glow stays quiet after the user finishes (or skips) it. Per-device
	 * by design: the beta flag is tenant-level, but "I've seen this" is a personal
	 * client preference — mirrors beta-announcement's `acknowledged`.
	 */
	seen: Partial<Record<TourId, boolean>>
}

type GuidedTourActions = {
	/** Start a tour: mark it active so the lazy host mounts and runs it. */
	start: (tourId: TourId) => void
	/**
	 * End a tour AND mark it seen (quiets the glow). Used for finish, skip, and
	 * the graceful end when a last step's target never appears.
	 */
	finish: (tourId: TourId) => void
	/** End a tour WITHOUT marking it seen (e.g. the user navigated away mid-tour). */
	dismiss: () => void
	/**
	 * Arm the "this route change is the tour's own doing" flag. Called for the
	 * caller by the `navigate` the engine hands to a step's `before` hook, so a
	 * step author cannot forget it.
	 */
	beginScriptedNavigation: () => void
	/** Disarm it — the host calls this once it has observed the route change. */
	endScriptedNavigation: () => void
}

type GuidedTourStore = GuidedTourState & { actions: GuidedTourActions }

const STORAGE_KEY = '@bitlyft/guided-tour'

/**
 * Backs the guided-tour engine. The trigger button calls `start`; the host
 * reads `activeTourId` to know which tour to run and calls `finish`/`dismiss`
 * when it ends. Only the per-tour `seen` map is persisted.
 */
export const useGuidedTourStore = createWithEqualityFn<GuidedTourStore>()(
	persist(
		(set) => ({
			activeTourId: null,
			isScriptedNavigation: false,
			seen: {},
			actions: {
				start: (tourId) =>
					set({ activeTourId: tourId, isScriptedNavigation: false }),
				finish: (tourId) =>
					set((state) => ({
						activeTourId: null,
						isScriptedNavigation: false,
						seen: { ...state.seen, [tourId]: true },
					})),
				dismiss: () => set({ activeTourId: null, isScriptedNavigation: false }),
				beginScriptedNavigation: () => set({ isScriptedNavigation: true }),
				endScriptedNavigation: () => set({ isScriptedNavigation: false }),
			},
		}),
		{
			name: STORAGE_KEY,
			// Persist only the per-tour seen map; active tour + actions stay in memory.
			partialize: (state) => ({ seen: state.seen }),
		},
	),
	shallow,
)

/** The tour currently running, or null. */
export const useActiveTourId = () => useGuidedTourStore((s) => s.activeTourId)

/** Whether the pending route change was initiated by the tour itself. */
export const useScriptedNavigation = () =>
	useGuidedTourStore((s) => s.isScriptedNavigation)

/** Whether a given tour has been taken at least once (on this device). */
export const useTourSeen = (tourId: TourId) =>
	useGuidedTourStore((s) => Boolean(s.seen[tourId]))

/** Tour actions (start/finish/dismiss) — stable, safe to read once. */
export const useGuidedTourActions = () => useGuidedTourStore((s) => s.actions)

/**
 * Start a tour from outside React — the named vanilla accessor callers use
 * instead of reaching through `useGuidedTourStore.getState()` themselves
 * (mirrors `setCurrentTenant`, `getAIStore`). The demo's completion toast needs
 * this: it offers "Replay tour" from inside a Sonner render, not a component
 * that can hold the hook.
 */
export const startTour = (tourId: TourId) =>
	useGuidedTourStore.getState().actions.start(tourId)
